import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Course, ContextCardSection } from '@shared/models/course.model';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { from } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

@Component({
  selector: 'context-card-edit',
  templateUrl: './context-card-edit.component.html',
  styleUrls: ['./context-card-edit.component.scss']
})
export class ContextCardEditComponent implements OnInit, OnChanges {
  @Input() course: Course;
  @Output() courseChanged = new EventEmitter<Course>();

  contextCardForm: FormGroup;
  sections: ContextCardSection[] = [];
  newSectionName = '';

  constructor(private _courseApiService: CoursesAPIService) {
    this.contextCardForm = new FormGroup({
      enabled: new FormControl(false),
      title: new FormControl('', { updateOn: 'blur' })
    });
  }

  ngOnInit(): void {
    this.contextCardForm.valueChanges.pipe(
      mergeMap(() => from(Object.entries(this.contextCardForm.controls))),
      filter(([, control]) => (control as FormControl).dirty),
      switchMap(([label, control]) => {
        (control as FormControl).markAsPristine();
        if (label === 'enabled') {
          this._toggleFormFields(control.value);
        }
        return this._courseApiService.patchCourse(this.course.id, {
          op: 'replace',
          path: `information.contextCard.${label}`,
          value: control.value
        });
      })
    ).subscribe((course) => this.courseChanged.emit(course));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['course']?.currentValue) {
      const cc = this.course.information?.contextCard;
      this.contextCardForm.reset({ enabled: cc?.enabled ?? false, title: cc?.title ?? '' }, { emitEvent: false });
      this._toggleFormFields(cc?.enabled ?? false);
      this.sections = [...(cc?.sections ?? [])];
    }
  }

  get isEnabled(): boolean {
    return this.contextCardForm.controls['enabled'].value;
  }

  trackByIndex(index: number): number {
    return index;
  }

  canAddSection(): boolean {
    return this.sections.length < 4;
  }

  canDeleteSection(): boolean {
    return this.sections.length > 1;
  }

  addSection(): void {
    const name = this.newSectionName.trim();
    if (!name) return;
    const path = `information.contextCard.sections[${this.sections.length}]`;
    const value = new ContextCardSection(name, '');
    this._courseApiService.patchCourse(this.course.id, { op: 'add', path, value })
      .subscribe((course) => {
        this.sections = [...(course.information?.contextCard?.sections ?? [])];
        this.newSectionName = '';
      });
  }

  removeSection(index: number): void {
    const path = `information.contextCard.sections[${index}]`;
    this._courseApiService.patchCourse(this.course.id, { op: 'remove', path })
      .subscribe((course) => {
        this.sections = [...(course.information?.contextCard?.sections ?? [])];
      });
  }

  sectionChanged(event: { data: ContextCardSection; index: number }): void {
    const path = `information.contextCard.sections[${event.index}]`;
    this._courseApiService.patchCourse(this.course.id, { op: 'replace', path, value: event.data })
      .subscribe((course) => {
        this.sections = [...(course.information?.contextCard?.sections ?? [])];
      });
  }

  _toggleFormFields(enabled: boolean): void {
    const method = enabled ? 'enable' : 'disable';
    this.contextCardForm.controls['title'][method]({ emitEvent: false });
  }
}
